/**
 * Lightweight 3D vector backed by Float32Array.
 * All methods return `this` for chaining. Scratch instances
 * are used in hot paths to avoid GC pressure.
 */
export class Vec3 {
  readonly data: Float32Array;

  constructor(x = 0, y = 0, z = 0) {
    this.data = new Float32Array([x, y, z]);
  }

  get x(): number { return this.data[0]; }
  get y(): number { return this.data[1]; }
  get z(): number { return this.data[2]; }
  set x(v: number) { this.data[0] = v; }
  set y(v: number) { this.data[1] = v; }
  set z(v: number) { this.data[2] = v; }

  set(x: number, y: number, z: number): this {
    this.data[0] = x; this.data[1] = y; this.data[2] = z;
    return this;
  }

  copy(v: Vec3): this {
    this.data[0] = v.data[0]; this.data[1] = v.data[1]; this.data[2] = v.data[2];
    return this;
  }

  add(v: Vec3): this {
    this.data[0] += v.data[0]; this.data[1] += v.data[1]; this.data[2] += v.data[2];
    return this;
  }

  sub(v: Vec3): this {
    this.data[0] -= v.data[0]; this.data[1] -= v.data[1]; this.data[2] -= v.data[2];
    return this;
  }

  scale(s: number): this {
    this.data[0] *= s; this.data[1] *= s; this.data[2] *= s;
    return this;
  }

  dot(v: Vec3): number {
    return this.data[0] * v.data[0] + this.data[1] * v.data[1] + this.data[2] * v.data[2];
  }

  cross(a: Vec3, b: Vec3): this {
    const ax = a.data[0], ay = a.data[1], az = a.data[2];
    const bx = b.data[0], by = b.data[1], bz = b.data[2];
    this.data[0] = ay * bz - az * by;
    this.data[1] = az * bx - ax * bz;
    this.data[2] = ax * by - ay * bx;
    return this;
  }

  length(): number {
    const d = this.data;
    return Math.sqrt(d[0] * d[0] + d[1] * d[1] + d[2] * d[2]);
  }

  normalize(): this {
    const len = this.length();
    if (len > 1e-6) this.scale(1 / len);
    return this;
  }

  /** Pre-allocated scratch vectors for temp calculations. Never store refs to these. */
  static readonly SCRATCH_A = new Vec3();
  static readonly SCRATCH_B = new Vec3();
  static readonly SCRATCH_C = new Vec3();
}
